/**
 * Engagement detail (WS-5.2): header + ICF rules + sessions table + CRF panel,
 * with the analysis modal preset to this engagement for "Upload session".
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTabAccess } from '@/hooks/useTabAccess';
import { WorkspaceModal } from '@/components/mentorEngagements/WorkspaceModal';
import { EngagementOverviewPanel } from '@/components/mentorEngagements/EngagementOverviewPanel';
import { PrioritiesModal } from '@/components/mentorEngagements/PrioritiesModal';
import { nextActionTarget, type WorkspaceTab } from '@/components/mentorEngagements/overview';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';

import { reviewDesk } from '@/design-system';
import { WebAppShell } from '@/design-system/components';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/context/ToastContext';
import { ROUTES } from '@/navigation/routes';
import { trackAuthEvent } from '@/services/analytics';
import { documentsAPI, taskAPI, transcriptionAPI } from '@/services/api';
import { derivedIdempotencyKey, useIdempotencyIntent } from '@/services/idempotencyIntent';
import { sessionFileKind, taskOutcome } from '@/utils/mentorFormWorkflow';
import { MentorEngagementsAPI } from '@/services/api/mentorEngagementsAPI';
import { ICF_FORMS } from '@/data/icfFormStatements';
import { confirmDialog, showDialog } from '@/utils/dialogs';
import { downloadBlob } from '@/utils/downloadBlob';
import type { AnalysisOptions } from '@/types';
import type {
  CompetencyReviewForm,
  CrfVerdict,
  EngagementDetail,
  EngagementOverview,
  EngagementSessionRow,
  EngagementStatus,
  FormCompletionData,
  SessionObservationForm,
  SessionTranscript,
  SessionUpdateRequest,
  SofUpdateRequest,
} from '@/types/mentorEngagements';
import { AnalysisModal } from '@/components/analysis/AnalysisModal';
import { AnalysisProgress, TranscriptionProgress } from '@/components/voice';
import { ActionButton, Chip, Panel, SegmentedField, mentorStyles } from '@/components/mentorEngagements/shared';
import { EngagementHeaderPanel } from '@/components/mentorEngagements/EngagementHeaderPanel';
import { SessionsTable } from '@/components/mentorEngagements/SessionsTable';
import { ManualSessionModal, type ManualSessionPayload } from '@/components/mentorEngagements/ManualSessionModal';
import { SessionMetaModal } from '@/components/mentorEngagements/SessionMetaModal';
import { SofReviewModal } from '@/components/mentorEngagements/SofReviewModal';
import { FinalizeModal } from '@/components/mentorEngagements/FinalizeModal';
import { EmailDeliveryModal } from '@/components/mentorEngagements/EmailDeliveryModal';
import { CrfPanel } from '@/components/mentorEngagements/CrfPanel';
import { workspaceDeepLink } from '@/components/mentorEngagements/setupChecklist';
import { trialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';
import { noCreditMessage } from '../../utils/creditErrors';
import {
  audioUploadRejectionCode,
  audioUploadRejectionMessage,
  transcriptionFailureEvent,
  voiceMinutesExceededMessage,
} from '../../utils/transcribeError';

const NARRATIVE_POLL_MS = 3000;
const NARRATIVE_POLL_LIMIT = 20;

type FinalizeTarget = { kind: 'sof'; session: EngagementSessionRow } | { kind: 'crf'; verdict: CrfVerdict };

function firstParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

export default function MentorEngagementDetailScreen() {
  const params = useLocalSearchParams<{ id?: string | string[]; action?: string | string[]; session?: string | string[] }>();
  const router = useRouter();
  const { t, language } = useTranslation();
  const { showToast } = useToast();
  const { isPhone, isTablet } = useResponsive();
  const engagementId = Number(firstParam(params.id));

  const { user, isSchool, isSuper } = useTabAccess();
  const [tab, setTab] = useState<WorkspaceTab>('overview');
  const [overview, setOverview] = useState<EngagementOverview | null>(null);
  const [overviewError, setOverviewError] = useState(false);
  const [editingEngagement, setEditingEngagement] = useState(false);
  const [editingPriorities, setEditingPriorities] = useState(false);
  const [sharingBusy, setSharingBusy] = useState(false);
  const [practiceSession, setPracticeSession] = useState<number | undefined>();
  const tabKey = `mentor-workspace-tab:${user?.id ?? 'anonymous'}:${engagementId}`;
  const tabTouched = useRef(false);
  useEffect(() => {
    let active = true;
    tabTouched.current = false;
    setTab('overview');
    AsyncStorage.getItem(tabKey).then(value => {
      if (active && !tabTouched.current && (value === 'overview' || value === 'sessions' || value === 'documents')) setTab(value);
    }).catch(() => {});
    return () => { active = false; };
  }, [tabKey]);
  const selectTab = (value: WorkspaceTab) => {
    tabTouched.current = true;
    setTab(value);
    void AsyncStorage.setItem(tabKey, value).catch(() => {});
  };
  const [engagement, setEngagement] = useState<EngagementDetail | null>(null);
  const [crf, setCrf] = useState<CompetencyReviewForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [crfLoading, setCrfLoading] = useState(true);
  const [crfError, setCrfError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadVisible, setUploadVisible] = useState(false);
  // Setup-checklist "add a session" lands here. Browsers only open a file picker from a click, so
  // the deep link shows a one-click upload prompt instead of opening the picker itself.
  const [addSessionPromptVisible, setAddSessionPromptVisible] = useState(false);
  const [manualVisible, setManualVisible] = useState(false);
  const [editingSession, setEditingSession] = useState<EngagementSessionRow | null>(null);
  const [metaSession, setMetaSession] = useState<EngagementSessionRow | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [manualDraft, setManualDraft] = useState<Partial<ManualSessionPayload> | undefined>();
  const createdManualSession = useRef<EngagementSessionRow | null>(null);
  // ASYNC-6: one Idempotency-Key per upload intent (a double submit of the
  // same file replays one job); "Try again" mints a new key.
  const uploadIntent = useIdempotencyIntent();
  /** Re-dispatches the analysis shown in the overlay ("Try again"). */
  const analysisRetryRef = useRef<
    ((idempotencyKey: string) => Promise<{ task_id?: string | null; session_id?: number | null }>) | null
  >(null);
  const retryInFlight = useRef(false);
  const [review, setReview] = useState<{ session: EngagementSessionRow; form: SessionObservationForm; transcript?: SessionTranscript } | null>(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  const reviewRequest = useRef(0);
  const [emailVisible, setEmailVisible] = useState(false);
  const [finalizeTarget, setFinalizeTarget] = useState<FinalizeTarget | null>(null);
  const [pendingUpload, setPendingUpload] = useState<DocumentPicker.DocumentPickerAsset | null>(null);
  // Live progress for a mentee session review (same WebSocket-driven overlays as the coach flow).
  const [transcriptionTaskId, setTranscriptionTaskId] = useState<string | null>(null);
  const [transcriptionVisible, setTranscriptionVisible] = useState(false);
  const [analysisTaskId, setAnalysisTaskId] = useState<string | null>(null);
  const [analysisSessionId, setAnalysisSessionId] = useState<number | null>(null);
  const [analysisVisible, setAnalysisVisible] = useState(false);
  const [analysisDocumentName, setAnalysisDocumentName] = useState<string | undefined>();
  const cancelledTaskIds = useRef<Set<string>>(new Set());
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const narrativeBaseline = useRef<string | null>(null);

  const loadOverview = useCallback(async () => {
    try { setOverview(await MentorEngagementsAPI.getOverview(engagementId)); setOverviewError(false); }
    catch { setOverviewError(true); }
  }, [engagementId]);

  const loadEngagement = useCallback(async () => {
    try {
      setEngagement(await MentorEngagementsAPI.get(engagementId));
      setError(null);
    } catch {
      setError(t('mentorEngagements.detail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [engagementId, t]);

  const loadCrf = useCallback(async () => {
    try {
      setCrf(await MentorEngagementsAPI.getCrf(engagementId));
      setCrfError(null);
    } catch {
      setCrfError(t('mentorEngagements.detail.loadError'));
    } finally {
      setCrfLoading(false);
    }
  }, [engagementId, t]);

  const refresh = useCallback(async () => {
    await Promise.all([loadEngagement(), loadOverview()]);
  }, [loadEngagement, loadOverview]);

  useEffect(() => {
    if (!Number.isFinite(engagementId)) {
      setError(t('mentorEngagements.detail.loadError'));
      setLoading(false);
      setCrfLoading(false);
      return;
    }
    refresh();
  }, [engagementId, refresh, t]);

  useEffect(() => {
    if (overview?.next_action) void loadCrf();
    else if (overview) setTab('overview');
  }, [overview, loadCrf]);

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  // ── Sessions ───────────────────────────────────────────────────────────────

  const handleToggleInclude = async (session: EngagementSessionRow, include: boolean) => {
    await MentorEngagementsAPI.updateSession(engagementId, session.id, { include_in_crf: include });
    await refresh();
  };

  const handleRemoveSession = async (session: EngagementSessionRow) => {
    const confirmed = await confirmDialog(
      t('mentorEngagements.sessions.removeConfirmTitle'),
      t('mentorEngagements.sessions.removeConfirmBody'),
      t('mentorEngagements.sessions.remove'),
      t('mentorEngagements.create.cancel'),
    );
    if (!confirmed) return;
    await MentorEngagementsAPI.removeSession(engagementId, session.id);
    await refresh();
  };

  const handleDownloadSof = async (session: EngagementSessionRow) => {
    try {
      const blob = await MentorEngagementsAPI.sofPdf(engagementId, session.id, language);
      await downloadBlob(blob, `sof-session-${session.session_number}.pdf`, t('mentorEngagements.sessions.downloadSof'));
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      showDialog(t(status === 409 ? 'mentorEngagements.toast.legacyPdf' : 'mentorEngagements.toast.downloadError'));
    }
  };

  const handleReopenSof = async (session: EngagementSessionRow) => {
    const confirmed = await confirmDialog(
      t('mentorEngagements.finalize.reopenConfirmTitle'),
      t('mentorEngagements.finalize.reopenConfirmBody'),
      t('mentorEngagements.sessions.reopen'),
      t('mentorEngagements.create.cancel'),
    );
    if (!confirmed) return;
    setReviewSaving(true);
    try {
      const form = await MentorEngagementsAPI.reopenSof(engagementId, session.id);
      setReview(current => current?.session.id === session.id ? { ...current, form } : current);
      await refresh();
    } catch { showDialog(t('mentorEngagements.review.reopenError')); }
    finally { setReviewSaving(false); }
  };

  const handleReview = async (session: EngagementSessionRow) => {
    const request = ++reviewRequest.current;
    try {
      const form = await MentorEngagementsAPI.getSof(engagementId, session.id);
      if (request === reviewRequest.current) setReview({ session, form });
      // Transcript is best-effort: the mentor-scoped endpoint may not exist yet; the sheet shows "not available".
      if (session.source === 'mcai') {
        const transcript = await MentorEngagementsAPI.getSessionTranscript(engagementId, session.id).catch(() => undefined);
        if (transcript && request === reviewRequest.current) setReview(current => current ? { ...current, transcript } : null);
      }
    } catch { showDialog(t('mentorEngagements.review.loadError')); }
  };

  const handleSaveReview = async (update: SofUpdateRequest) => {
    if (!review || reviewSaving || engagement?.status === 'archived') return;
    setReviewSaving(true);
    try {
      const form = await MentorEngagementsAPI.updateSof(engagementId, review.session.id, update);
      setReview(current => current ? { ...current, form } : null);
      trackAuthEvent('sof_reviewed_saved', { engagement_id: engagementId, session_id: review.session.id, source: review.session.source, level: form.level });
      await refresh();
    } finally { setReviewSaving(false); }
  };

  const handleAddManual = async (payload: ManualSessionPayload) => {
    // Keep the created row on a failed SOF save so retry cannot create duplicates.
    const session = editingSession || createdManualSession.current || await MentorEngagementsAPI.addSession(engagementId, {
      source: 'manual',
      session_date: payload.session_date,
      modality: payload.modality,
      duration_minutes: payload.duration_minutes,
    });
    if (!editingSession) createdManualSession.current = session;
    if (editingSession) await MentorEngagementsAPI.updateSession(engagementId, session.id, {
      session_date: payload.session_date, modality: payload.modality, duration_minutes: payload.duration_minutes,
    });
    await MentorEngagementsAPI.updateSof(engagementId, session.id, {
      mentor_ratings: payload.mentor_ratings,
      c1_q1: payload.c1_q1,
      c1_q2: payload.c1_q2,
    });
    createdManualSession.current = null;
    trackAuthEvent('engagement_session_added', { source: 'manual', engagement_id: engagementId });
    await refresh();
  };

  // Date / modality / duration only — never touches SOF ratings, so it is safe for AI sessions.
  const handleSaveSessionMeta = async (session: EngagementSessionRow, payload: SessionUpdateRequest) => {
    await MentorEngagementsAPI.updateSession(engagementId, session.id, payload);
    trackAuthEvent('engagement_session_meta_edited', { engagement_id: engagementId, session_id: session.id, source: session.source });
    await refresh();
  };

  // ── Status flow ────────────────────────────────────────────────────────────

  const handleStatusChange = async (status: EngagementStatus) => {
    if (!engagement || statusBusy) return;
    const key = status === 'completed' ? 'Complete' : status === 'archived' ? 'Archive' : 'Reactivate';
    const confirmed = await confirmDialog(
      t(`mentorEngagements.statusFlow.confirm${key}Title`),
      t(`mentorEngagements.statusFlow.confirm${key}Body`),
      t(status === 'completed' ? 'mentorEngagements.statusFlow.markCompleted' : status === 'archived' ? 'mentorEngagements.statusFlow.archive' : 'mentorEngagements.statusFlow.reactivate'),
      t('mentorEngagements.create.cancel'),
    );
    if (!confirmed) return;
    setStatusBusy(true);
    try {
      await MentorEngagementsAPI.update(engagementId, { status });
      trackAuthEvent('engagement_status_changed', { engagement_id: engagementId, status, previous: engagement.status });
      await refresh();
    } catch {
      showDialog(t('mentorEngagements.statusFlow.error'));
    } finally {
      setStatusBusy(false);
    }
  };

  const handleEditManual = async (session: EngagementSessionRow) => {
    try {
      const sof = await MentorEngagementsAPI.getSof(engagementId, session.id);
      setEditingSession(session);
      setManualDraft({ session_date: session.session_date || '', modality: session.modality || 'virtual',
        duration_minutes: session.duration_minutes || 0, mentor_ratings: sof.mentor_ratings || {},
        c1_q1: sof.c1_q1, c1_q2: sof.c1_q2 });
      setManualVisible(true);
    } catch { showDialog(t('mentorEngagements.detail.loadError')); }
  };

  const handleUploadSession = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({ type: '*/*', copyToCacheDirectory: true });
      if (result.canceled || !result.assets?.length) return;
      if (!sessionFileKind(result.assets[0].name)) {
        showDialog(t('mentorEngagements.sessions.unsupportedFile'));
        return;
      }
      setPendingUpload(result.assets[0]);
      setUploadVisible(true);
    } catch {
      showDialog(t('mentorEngagements.sessions.uploadError'));
    }
  };

  const handleAnalysisSubmit = async (options: AnalysisOptions) => {
    if (!pendingUpload || uploadBusy) return;
    const file = pendingUpload.file
      ? pendingUpload.file
      : { uri: pendingUpload.uri, type: pendingUpload.mimeType || 'application/octet-stream', name: pendingUpload.name };
    const kind = sessionFileKind(pendingUpload.name);
    const documentName = pendingUpload.name;
    const analysisOptions = { ...options, engagementId, includeInIcfProgress: false };
    const input = file as File | { uri: string; type: string; name: string };
    const idempotencyKey = uploadIntent.key(pendingUpload);
    setUploadBusy(true);
    setUploadStatus(t('mentorEngagements.sessions.uploadStarted'));
    setAnalysisDocumentName(documentName);
    const showAnalysis = (task: { task_id?: string | null; session_id?: number | null }) => {
      setAnalysisTaskId(task.task_id || null);
      setAnalysisSessionId(task.session_id || null);
      setAnalysisVisible(true);
    };
    try {
      if (kind === 'audio') {
        const transcript = await transcriptionAPI.transcribe(input, {
          uiLanguage: language, enableDiarization: true, includeInICFProgress: false, idempotencyKey,
        });
        setTranscriptionTaskId(transcript.task_id || null);
        setTranscriptionVisible(true);
        // Resolve now so the setup modal closes; the transcription overlay
        // takes over and chains into the analysis overlay when it is done.
        void (async () => {
          try {
            const deadline = Date.now() + 45 * 60_000;
            while (Date.now() < deadline) {
              if (transcript.task_id && cancelledTaskIds.current.has(transcript.task_id)) return;
              const status = await transcriptionAPI.getStatus(transcript.document_id);
              const outcome = taskOutcome(status.status);
              if (outcome === 'failed') throw new Error(status.error || 'Transcription failed');
              if (outcome === 'complete') break;
              await new Promise(resolve => setTimeout(resolve, 3000));
            }
            setTranscriptionVisible(false);
            // Retry re-analyzes the finished transcript (no second upload).
            const analyzeTranscript = (key: string) =>
              transcriptionAPI.analyzeTranscript(transcript.document_id, { ...analysisOptions, idempotencyKey: key });
            analysisRetryRef.current = analyzeTranscript;
            showAnalysis(await analyzeTranscript(derivedIdempotencyKey(idempotencyKey, 'analysis')));
          } catch (error) {
            uploadIntent.reset();
            setTranscriptionVisible(false);
            setUploadStatus(
              trialAnalysisUsedMessage(t, language, error)
              || noCreditMessage(t, error)
              || voiceMinutesExceededMessage(t, error)
              || t('mentorEngagements.sessions.uploadError'),
            );
            setUploadBusy(false);
          }
        })();
        return;
      }
      const analyzeDocument = (key: string) =>
        documentsAPI.analyze(input, { ...analysisOptions, idempotencyKey: key });
      analysisRetryRef.current = analyzeDocument;
      showAnalysis(await analyzeDocument(idempotencyKey));
    } catch (error) {
      if (audioUploadRejectionCode(error)) {
        trackAuthEvent('audio_upload_rejected', transcriptionFailureEvent(error, 'mentor_engagement').properties);
      }
      setUploadStatus(
        trialAnalysisUsedMessage(t, language, error)
        || noCreditMessage(t, error)
        || voiceMinutesExceededMessage(t, error)
        || audioUploadRejectionMessage(t, error)
        || t('mentorEngagements.sessions.uploadError'),
      );
      setUploadBusy(false);
      throw error;
    }
  };

  const finishAnalysisOverlay = () => {
    uploadIntent.reset();
    analysisRetryRef.current = null;
    setAnalysisVisible(false);
    setAnalysisTaskId(null);
    setAnalysisSessionId(null);
    setAnalysisDocumentName(undefined);
    setUploadBusy(false);
  };

  // "Try again" on a failed analysis: same input, NEW Idempotency-Key (the old
  // key would replay the failed job).
  const handleAnalysisRetry = async () => {
    const dispatch = analysisRetryRef.current;
    if (retryInFlight.current) return;
    if (!dispatch) {
      finishAnalysisOverlay();
      return;
    }
    retryInFlight.current = true;
    setUploadBusy(true);
    setUploadStatus(t('mentorEngagements.sessions.uploadStarted'));
    setAnalysisTaskId(null);
    setAnalysisSessionId(null);
    try {
      const task = await dispatch(uploadIntent.renew());
      setAnalysisTaskId(task.task_id || null);
      setAnalysisSessionId(task.session_id || null);
    } catch (error) {
      setUploadStatus(trialAnalysisUsedMessage(t, language, error) || noCreditMessage(t, error) || t('mentorEngagements.sessions.uploadError'));
      finishAnalysisOverlay();
    } finally {
      retryInFlight.current = false;
    }
  };

  const handleAnalysisComplete = async (sessionId: number) => {
    uploadIntent.reset();
    setAnalysisSessionId(sessionId);
    await refresh();
    trackAuthEvent('engagement_session_added', { source: 'mcai', engagement_id: engagementId });
    setPendingUpload(null);
    setUploadStatus(t('mentorEngagements.sessions.uploadComplete'));
    setTimeout(finishAnalysisOverlay, 1200);
  };

  const handleAnalysisCancel = async (taskId?: string | null) => {
    if (taskId) {
      cancelledTaskIds.current.add(taskId);
      try { await taskAPI.cancel(taskId); } catch { /* the overlay already closed; nothing else to undo */ }
    }
    setTranscriptionVisible(false);
    finishAnalysisOverlay();
    setUploadStatus(null);
  };

  // ── CRF ────────────────────────────────────────────────────────────────────

  const handleSaveCrf = async (payload: {
    c1_verified: boolean | null;
    c1_notes: string;
    cumulative_feedback: CompetencyReviewForm['cumulative_feedback'];
  }) => {
    const saved = await MentorEngagementsAPI.updateCrf(engagementId, {
      overrides: { c1_verified: payload.c1_verified, c1_notes: payload.c1_notes },
      cumulative_feedback: payload.cumulative_feedback || undefined,
    });
    setCrf(saved);
    await Promise.all([loadEngagement(), loadOverview()]);
    return saved;
  };

  const handleToggleCrfStatement = async (statementId: string, next: boolean) => {
    const saved = await MentorEngagementsAPI.updateCrf(engagementId, { overrides: { statements: { [statementId]: next } } });
    setCrf(saved);
    const result = saved.computed.statement_results[statementId];
    trackAuthEvent('crf_statement_override_toggled', {
      engagement_id: engagementId, statement_id: statementId, level: saved.level,
      effective: next, computed: result?.computed ?? null, reset: result ? result.effective === result.computed : false,
    });
    await loadOverview();
    return saved;
  };

  const pollNarrative = useCallback((attempt: number) => {
    if (attempt > NARRATIVE_POLL_LIMIT) {
      setCrfError(t('mentorEngagements.crf.narrativeError'));
      return;
    }
    pollTimer.current = setTimeout(async () => {
      try {
        const next = await MentorEngagementsAPI.getCrf(engagementId);
        setCrf(next);
        if (next.narrative_pending) pollNarrative(attempt + 1);
        else if (!next.narrative_draft?.generated_at || next.narrative_draft.generated_at === narrativeBaseline.current) {
          showDialog(t('mentorEngagements.crf.narrativeError'));
        } else {
          void loadOverview();
          trackAuthEvent('crf_narrative_generated', { engagement_id: engagementId, level: next.level });
        }
      } catch {
        setCrfError(t('mentorEngagements.detail.loadError'));
      }
    }, NARRATIVE_POLL_MS);
  }, [engagementId, loadOverview, t]);

  const handleGenerateNarrative = async () => {
    try {
      if (pollTimer.current) clearTimeout(pollTimer.current);
      setCrfError(null);
      narrativeBaseline.current = crf?.narrative_draft?.generated_at || null;
      await MentorEngagementsAPI.generateCrfNarrative(engagementId);
      trackAuthEvent('crf_narrative_requested', { engagement_id: engagementId, level: engagement?.target_level });
      setCrf((current) => (current ? { ...current, narrative_pending: true } : current));
      pollNarrative(1);
    } catch {
      showDialog(t('mentorEngagements.crf.narrativeError'));
    }
  };

  const handleDownloadCrf = async () => {
    try {
      const blob = await MentorEngagementsAPI.crfPdf(engagementId, language);
      await downloadBlob(blob, `crf-${engagementId}.pdf`, t('mentorEngagements.crf.downloadPdf'));
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      showDialog(t(status === 409 ? 'mentorEngagements.toast.legacyPdf' : 'mentorEngagements.toast.downloadError'));
    }
  };

  const handleReopenCrf = async () => {
    const confirmed = await confirmDialog(
      t('mentorEngagements.finalize.reopenConfirmTitle'),
      t('mentorEngagements.finalize.reopenConfirmBody'),
      t('mentorEngagements.crf.reopen'),
      t('mentorEngagements.create.cancel'),
    );
    if (!confirmed) return;
    setCrf(await MentorEngagementsAPI.reopenCrf(engagementId));
    await loadOverview();
  };

  const handleFinalizeConfirm = async (printedName: string, signedDate: string, completion: FormCompletionData) => {
    if (!finalizeTarget) return;
    if (finalizeTarget.kind === 'sof') {
      const form = await MentorEngagementsAPI.finalizeSof(engagementId, finalizeTarget.session.id, {
        mentor_printed_name: printedName,
        signed_date: signedDate,
        completion_data: completion,
      });
      setReview(current => current?.session.id === finalizeTarget.session.id ? { ...current, form } : current);
      trackAuthEvent('sof_finalized', { engagement_id: engagementId, level: engagement?.target_level });
    } else {
      await MentorEngagementsAPI.finalizeCrf(engagementId, {
        mentor_printed_name: printedName,
        signed_date: signedDate,
        completion_data: completion,
        overall_verdict: finalizeTarget.verdict,
      });
      trackAuthEvent('crf_finalized', { engagement_id: engagementId, verdict: finalizeTarget.verdict });
    }
    await refresh();
  };

  const handleSendEmail = async (documents: string[]) => {
    const delivery = await MentorEngagementsAPI.send(engagementId, { documents });
    if (!delivery.sent) throw new Error('Email was not sent');
    trackAuthEvent('mentor_forms_emailed', { engagement_id: engagementId, documents: documents.length });
    showToast(t('mentorEngagements.email.sent', { email: engagement?.mentee_email || '' }), 'success');
  };

  const isOwner = engagement?.mentor_user_id != null ? engagement.mentor_user_id === user?.id : !isSchool && !isSuper;
  const canEdit = Boolean(isOwner && engagement?.status !== 'archived');
  // Redacted rows (school admin, not shared) carry `mentee_name: null`.
  const menteeLabel = engagement?.mentee_name || t('mentorEngagements.privateMentee');
  // Setup-checklist "do it now" links (`?action=`): open the exact tab/modal once the workspace has loaded.
  const deepLink = workspaceDeepLink(firstParam(params.action), firstParam(params.session));
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (!deepLink || deepLinkHandled.current || !engagement || !overview) return;
    deepLinkHandled.current = true;
    router.setParams({ action: undefined, session: undefined } as never);
    if (!canEdit && deepLink.open !== 'share') return;
    selectTab(deepLink.tab);
    if (deepLink.open === 'add_session') setAddSessionPromptVisible(true);
    if (deepLink.open === 'review_sof') {
      const session = engagement.sessions.find(s => s.id === deepLink.sessionId);
      if (session) void handleReview(session);
    }
    if (deepLink.open === 'send') setEmailVisible(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink?.open, engagement, overview, canEdit]);

  const handleNext = () => {
    if (!overview?.next_action || !engagement) return;
    const target = nextActionTarget(overview.next_action);
    selectTab(target.tab);
    if (target.modal === 'add') { setEditingSession(null); setManualDraft(undefined); setManualVisible(true); }
    if (target.modal === 'priorities') setEditingPriorities(true);
    if (target.modal === 'engagement') setEditingEngagement(true);
    if (target.modal === 'review') {
      const session = engagement.sessions.find(s => s.id === target.sessionId) || engagement.sessions.find(s => s.sof_status === 'draft');
      if (session) void handleReview(session);
    }
    if (overview.next_action.key === 'record_practice') setPracticeSession(target.sessionId ?? engagement.sessions.at(-1)?.id);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  let content: React.ReactNode;
  if (loading) {
    content = <View style={mentorStyles.center}><ActivityIndicator color={reviewDesk.accent} /></View>;
  } else if (error || !engagement) {
    content = (
      <Panel testID="engagement_detail_error">
        <Text accessibilityRole="alert" style={mentorStyles.errorText}>{error || t('mentorEngagements.detail.loadError')}</Text>
        <View style={mentorStyles.row}>
          <ActionButton testID="engagement_detail_retry" icon="refresh" label={t('mentorEngagements.retry')} onPress={() => { setLoading(true); void refresh(); }} />
          <ActionButton label={t('mentorEngagements.back')} icon="arrow-back" onPress={() => router.push(ROUTES.MENTOR_ENGAGEMENTS as never)} />
        </View>
      </Panel>
    );
  } else {
    content = (
      <>
        <View style={mentorStyles.header}>
          <Pressable
            testID="engagement_back"
            onPress={() => router.push(ROUTES.MENTOR_ENGAGEMENTS as never)}
            accessibilityRole="button"
            style={({ pressed }) => [mentorStyles.row, { gap: 4 }, pressed && mentorStyles.pressed]}
          >
            <MaterialIcons name="arrow-back" size={18} color={reviewDesk.accent} />
            <Text style={{ color: reviewDesk.accent, fontWeight: '700', fontSize: 13 }}>
              {t('mentorEngagements.back')}
            </Text>
          </Pressable>
          {MentorEngagementsAPI.isMockEnabled ? (
            <Chip label={t('mentorEngagements.mockBanner')} tone="warning" icon="science" />
          ) : null}
        </View>

        <Text style={mentorStyles.subtitle}>{t('mentorEngagements.workspace.reviewing', { mentee: menteeLabel, level: engagement.target_level.toUpperCase(), revision: overview?.credential_goal.form_revision || engagement.form_revision || ICF_FORMS[engagement.target_level].sof_revision })}</Text>
        {engagement.status === 'archived' ? (
          <Panel testID="engagement_status_archived_notice">
            <View style={[mentorStyles.row, { gap: 6 }]}>
              <MaterialIcons name="archive" size={16} color={reviewDesk.muted} />
              <Text style={[mentorStyles.mutedText, mentorStyles.flexOne]}>{t('mentorEngagements.statusFlow.archivedNotice')}</Text>
            </View>
          </Panel>
        ) : engagement.status === 'completed' ? (
          <Panel testID="engagement_status_completed_notice">
            <View style={[mentorStyles.row, { gap: 6 }]}>
              <MaterialIcons name="check-circle" size={16} color={reviewDesk.success} />
              <Text style={[mentorStyles.mutedText, mentorStyles.flexOne]}>{t('mentorEngagements.statusFlow.completedNotice')}</Text>
            </View>
          </Panel>
        ) : null}
        {overviewError ? (
          <Panel testID="engagement_overview_error">
            <Text accessibilityRole="alert" style={mentorStyles.errorText}>{t('mentorEngagements.overviewLoadError')}</Text>
            <View style={mentorStyles.row}>
              <ActionButton testID="engagement_overview_retry" icon="refresh" label={t('mentorEngagements.retry')} onPress={() => void loadOverview()} />
            </View>
          </Panel>
        ) : null}
        {tab === 'overview' && !overview && !overviewError ? (
          <View style={mentorStyles.center}><ActivityIndicator color={reviewDesk.accent} /></View>
        ) : null}
        {tab === 'overview' && overview ? <EngagementOverviewPanel engagement={engagement} overview={overview} canEdit={canEdit} canShare={isOwner} busy={sharingBusy}
          onNext={handleNext} onEdit={() => setEditingEngagement(true)} onPriorities={() => setEditingPriorities(true)}
          onShare={async shared => {
            setSharingBusy(true);
            try { await MentorEngagementsAPI.update(engagementId, { shared_with_school: shared }); trackAuthEvent('engagement_sharing_changed', { shared }); await refresh(); }
            catch { showDialog(t('mentorEngagements.detail.saveError')); } finally { setSharingBusy(false); }
          }} /> : null}
        {editingEngagement ? <WorkspaceModal visible title={t('mentorEngagements.workspace.editEngagement')} onClose={() => setEditingEngagement(false)}>
          <EngagementHeaderPanel engagement={engagement} readOnly={!canEdit} onSave={async payload => { await MentorEngagementsAPI.update(engagementId, payload); await refresh(); setEditingEngagement(false); }} />
        </WorkspaceModal> : null}
        {editingPriorities ? <PrioritiesModal priorities={overview?.development_priorities || []} onClose={() => setEditingPriorities(false)} onSave={async development_priorities => {
          await MentorEngagementsAPI.updatePriorities(engagementId, { development_priorities });
          trackAuthEvent('engagement_priorities_set', { engagement_id: engagementId, count: development_priorities.length }); await refresh();
        }} /> : null}

        {tab === 'sessions' ? <>
        {addSessionPromptVisible && canEdit ? (
          <Panel testID="add_session_prompt">
            <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.sessions.addPromptTitle')}</Text>
            <Text style={mentorStyles.bodyText}>{t('mentorEngagements.sessions.addPromptBody')}</Text>
            <View style={mentorStyles.row}>
              <ActionButton
                testID="add_session_prompt_upload"
                variant="primary"
                icon="upload-file"
                label={t('mentorEngagements.sessions.addPromptUpload')}
                disabled={uploadBusy}
                onPress={() => { setAddSessionPromptVisible(false); void handleUploadSession(); }}
              />
              <ActionButton
                testID="add_session_prompt_manual"
                icon="edit-note"
                label={t('mentorEngagements.sessions.addPromptManual')}
                onPress={() => { setAddSessionPromptVisible(false); setEditingSession(null); setManualDraft(undefined); setManualVisible(true); }}
              />
            </View>
          </Panel>
        ) : null}
        <SessionsTable
          sessions={engagement.sessions}
          targetLevel={engagement.target_level}
          practiceSession={practiceSession}
          onSavePractice={async (session, payload) => {
            await MentorEngagementsAPI.updateSession(engagementId, session.id, payload);
            trackAuthEvent('engagement_practice_recorded', { engagement_id: engagementId, session_id: session.id, actions_count: payload.practice_actions?.length || 0 });
            await refresh();
          }}
          readOnly={!canEdit}
          onToggleInclude={handleToggleInclude}
          onDownloadSof={handleDownloadSof}
          onFinalizeSof={handleReview}
          onReopenSof={handleReopenSof}
          onRemove={handleRemoveSession}
          onUploadSession={() => { if (!uploadBusy) void handleUploadSession(); }}
          onAddManual={() => { setEditingSession(null); setManualDraft(undefined); setManualVisible(true); }}
          onEdit={handleEditManual}
          onEditMeta={setMetaSession}
          onReview={handleReview}
        />
        {uploadStatus ? <Text accessibilityLiveRegion="polite" style={mentorStyles.bodyText}>{uploadStatus}</Text> : null}
        <SessionMetaModal session={metaSession} onClose={() => setMetaSession(null)} onSave={handleSaveSessionMeta} />

        </> : null}

        {tab === 'documents' ? <>
        <Panel testID="workspace_missing_documentation">
          <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.workspace.missing')}</Text>
          {overview?.missing_documentation?.map((item, index) => <Text key={index} style={mentorStyles.bodyText}>{t(`mentorEngagements.workspace.missingItems.${item.key}`)}{item.session_id ? ` · ${t('mentorEngagements.workspace.sessionNumber', { number: engagement.sessions.find(s => s.id === item.session_id)?.session_number ?? '—' })}` : ''}</Text>)}
          {!overview?.missing_documentation?.length ? <Text style={mentorStyles.mutedText}>{t('mentorEngagements.workspace.documentationComplete')}</Text> : null}
          {engagement.sessions.filter(s => s.sof_status).map(session => <ActionButton key={session.id} label={t('mentorEngagements.workspace.sofExport', { number: session.session_number })} onPress={() => void handleDownloadSof(session)} />)}
        </Panel>
        <CrfPanel
          crf={crf}
          readOnly={!canEdit}
          loading={crfLoading}
          hasSessions={engagement.sessions.length > 0}
          loadError={crfError}
          onRetry={loadCrf}
          onSave={handleSaveCrf}
          onToggleStatement={handleToggleCrfStatement}
          onGenerateNarrative={handleGenerateNarrative}
          onFinalize={(verdict) => setFinalizeTarget({ kind: 'crf', verdict })}
          onReopen={handleReopenCrf}
          onDownloadPdf={handleDownloadCrf}
          onEmail={() => setEmailVisible(true)}
        />

        </> : null}

        {review ? <SofReviewModal
          visible={!finalizeTarget}
          level={engagement.target_level}
          language={language}
          session={review.session}
          form={review.form}
          transcript={review.transcript}
          readOnly={!canEdit}
          saving={reviewSaving}
          onClose={() => { reviewRequest.current++; setReview(null); }}
          onSave={handleSaveReview}
          onFinalize={() => setFinalizeTarget({ kind: 'sof', session: review.session })}
          onReopen={() => {
            if (engagement.status === 'archived') { showDialog(t('mentorEngagements.review.archived')); return; }
            void handleReopenSof(review.session);
          }}
          onDownloadPdf={() => void handleDownloadSof(review.session)}
        /> : null}

        <ManualSessionModal
          visible={manualVisible}
          level={engagement.target_level}
          initialPayload={manualDraft}
          onClose={() => setManualVisible(false)}
          onSave={handleAddManual}
        />

        <EmailDeliveryModal
          visible={emailVisible}
          recipient={engagement.mentee_email}
          sessions={engagement.sessions}
          onClose={() => setEmailVisible(false)}
          onSend={handleSendEmail}
        />

        <FinalizeModal
          visible={finalizeTarget !== null}
          defaultPrintedName={engagement.mentor_printed_name}
          warnings={finalizeTarget?.kind === 'crf' ? engagement.rule_checks : []}
          onClose={() => setFinalizeTarget(null)}
          onConfirm={handleFinalizeConfirm}
        />

        <AnalysisModal
          visible={uploadVisible}
          onClose={() => { setUploadVisible(false); setPendingUpload(null); }}
          onSubmit={handleAnalysisSubmit}
          transcriptName={pendingUpload?.name}
          engagementPreset={{
            id: engagement.id,
            mentee_name: menteeLabel,
            target_level: engagement.target_level,
            sof_revision: ICF_FORMS[engagement.target_level].sof_revision,
          }}
        />
        <TranscriptionProgress
          visible={transcriptionVisible}
          status={null}
          taskId={transcriptionTaskId}
          onCancel={taskId => { void handleAnalysisCancel(taskId); }}
        />
        <AnalysisProgress
          visible={analysisVisible}
          taskId={analysisTaskId}
          sessionId={analysisSessionId}
          documentName={analysisDocumentName}
          onComplete={sessionId => { void handleAnalysisComplete(sessionId); }}
          onError={() => { uploadIntent.reset(); setUploadStatus(t('mentorEngagements.sessions.uploadError')); setUploadBusy(false); }}
          onRetry={() => { void handleAnalysisRetry(); }}
          onClose={() => { finishAnalysisOverlay(); void refresh(); }}
          onCancel={taskId => { void handleAnalysisCancel(taskId); }}
        />
      </>
    );
  }

  return (
    <WebAppShell active="mentor-engagements" contentMaxWidth="100%" scroll={false}>
      {engagement ? <View style={{ paddingHorizontal: isPhone ? 16 : 28, paddingVertical: 12, gap: 8, backgroundColor: reviewDesk.canvas }}>
        <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
          <View style={[mentorStyles.row, { flexShrink: 1 }]}>
            <Text style={[mentorStyles.panelTitle, { flexShrink: 1 }]}>{menteeLabel} · {engagement.target_level.toUpperCase()}</Text>
            <View testID={`engagement_status_chip_${engagement.status}`}>
              <Chip
                label={t(`mentorEngagements.status.${engagement.status}`)}
                tone={engagement.status === 'active' ? 'accent' : engagement.status === 'completed' ? 'success' : 'neutral'}
                icon={engagement.status === 'archived' ? 'archive' : engagement.status === 'completed' ? 'check-circle' : undefined}
              />
            </View>
          </View>
          {/* Reactivate is gated on ownership, not `canEdit` (archived ⇒ canEdit false). */}
          {isOwner ? (
            <View style={mentorStyles.row}>
              {engagement.status === 'active' ? (
                <>
                  <ActionButton testID="engagement_status_complete" icon="check-circle" label={t('mentorEngagements.statusFlow.markCompleted')} onPress={() => void handleStatusChange('completed')} disabled={statusBusy} />
                  <ActionButton testID="engagement_status_archive" icon="archive" label={t('mentorEngagements.statusFlow.archive')} onPress={() => void handleStatusChange('archived')} disabled={statusBusy} />
                </>
              ) : engagement.status === 'completed' ? (
                <>
                  <ActionButton testID="engagement_status_reactivate" icon="replay" label={t('mentorEngagements.statusFlow.reactivate')} onPress={() => void handleStatusChange('active')} disabled={statusBusy} />
                  <ActionButton testID="engagement_status_archive" icon="archive" label={t('mentorEngagements.statusFlow.archive')} onPress={() => void handleStatusChange('archived')} disabled={statusBusy} />
                </>
              ) : (
                <ActionButton testID="engagement_status_reactivate" icon="replay" label={t('mentorEngagements.statusFlow.reactivate')} variant="primary" onPress={() => void handleStatusChange('active')} disabled={statusBusy} />
              )}
            </View>
          ) : null}
        </View>
        <SegmentedField testID="workspace_tabs" options={(['overview', 'sessions', 'documents'] as WorkspaceTab[]).filter(value => value === 'overview' || Boolean(overview?.next_action)).map(value => ({ value, label: t(`mentorEngagements.workspace.tabs.${value}`) }))} value={tab} onChange={selectTab} />
      </View> : null}
      <ScrollView
        testID="engagement_detail_screen"
        style={mentorStyles.screen}
        contentContainerStyle={[
          mentorStyles.content,
          isTablet && mentorStyles.contentTablet,
          isPhone && mentorStyles.contentPhone,
        ]}
      >
        {content}
      </ScrollView>
    </WebAppShell>
  );
}
